import React, { useCallback, useRef } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { Button, FormGroup, Input, Label } from "reactstrap";
import { useMeCore } from "../../../cores/me";
import { websitePageApiService } from "../../../cores/websitePage/services/api";
import { BLOCK_TYPES } from "../../../cores/websitePage/constants/blockTypes";
import { WebsiteBlock } from "../../../cores/websitePage/interfaces/WebsiteBlock";
import { WebsiteIcon } from "./WebsiteIcon";
import "./BlockProperties.scss";

interface Props {
  block: WebsiteBlock | null;
  onUpdate: (block: WebsiteBlock) => void;
  onDelete: (blockId: string) => void;
  onMoveUp: (blockId: string) => void;
  onMoveDown: (blockId: string) => void;
}

export function BlockProperties({ block, onUpdate, onDelete, onMoveUp, onMoveDown }: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = React.useState(false);

  const updateField = useCallback(
    (field: string, value: unknown) => {
      if (!block) return;
      onUpdate({ ...block, [field]: value });
    },
    [block, onUpdate]
  );

  const updateStyle = useCallback(
    (field: string, value: string) => {
      if (!block) return;
      onUpdate({
        ...block,
        styles: { ...block.styles, [field]: value },
      });
    },
    [block, onUpdate]
  );

  if (!block) {
    return (
      <div className="wp-properties wp-properties--empty">
        <div className="wp-properties__empty-icon">
          <WebsiteIcon icon="sliders-h" />
        </div>
        <h4>{intl.formatMessage({ id: "app.websitePage.editor.properties" })}</h4>
        <p>{intl.formatMessage({ id: "app.websitePage.editor.selectBlock" })}</p>
      </div>
    );
  }

  return (
    <div className="wp-properties">
      <div className="wp-properties__header">
        <div>
          <h6 className="wp-properties__title">{intl.formatMessage({ id: "app.websitePage.editor.properties" })}</h6>
          <span className="wp-properties__type">
            {(() => {
              const def = BLOCK_TYPES.find((b) => b.type === block.type);
              return def ? intl.formatMessage({ id: def.labelKey }) : block.type;
            })()}
          </span>
        </div>
        <div className="wp-properties__actions">
          <button
            type="button"
            className="wp-properties__action-btn"
            onClick={() => onMoveUp(block.id)}
            title={intl.formatMessage({ id: "app.websitePage.props.moveUp" })}
            aria-label={intl.formatMessage({ id: "app.websitePage.props.moveUp" })}
          >
            <WebsiteIcon icon="arrow-up" />
          </button>
          <button
            type="button"
            className="wp-properties__action-btn"
            onClick={() => onMoveDown(block.id)}
            title={intl.formatMessage({ id: "app.websitePage.props.moveDown" })}
            aria-label={intl.formatMessage({ id: "app.websitePage.props.moveDown" })}
          >
            <WebsiteIcon icon="arrow-down" />
          </button>
          <button
            type="button"
            className="wp-properties__action-btn wp-properties__action-btn--danger"
            onClick={() => onDelete(block.id)}
            title={intl.formatMessage({ id: "app.websitePage.props.delete" })}
            aria-label={intl.formatMessage({ id: "app.websitePage.props.delete" })}
          >
            <WebsiteIcon icon="trash" />
          </button>
        </div>
      </div>
      <div className="wp-properties__body">

      {(block.type === "header" || block.type === "text" || block.type === "footer") && (
        <FormGroup>
          <Label>{intl.formatMessage({ id: "app.websitePage.editor.content" })}</Label>
          <Input
            type="textarea"
            rows={3}
            value={block.content || ""}
            onChange={(e) => updateField("content", e.target.value)}
          />
        </FormGroup>
      )}

      {block.type === "header" && (
        <FormGroup>
          <Label>{intl.formatMessage({ id: "app.websitePage.props.level" })}</Label>
          <Input
            type="select"
            value={block.level || 2}
            onChange={(e) => updateField("level", Number(e.target.value))}
          >
            <option value={1}>H1</option>
            <option value={2}>H2</option>
            <option value={3}>H3</option>
          </Input>
        </FormGroup>
      )}

      {block.type === "html-page" && (
        <>
          <p className="text-muted small mb-3">
            {intl.formatMessage({ id: "app.websitePage.blocks.htmlPageHint" })}
          </p>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.htmlEditor.code" })}</Label>
            <Input
              type="textarea"
              rows={18}
              className="font-monospace"
              value={block.html || ""}
              onChange={(e) => updateField("html", e.target.value)}
              spellCheck={false}
            />
          </FormGroup>
        </>
      )}

      {block.type === "navbar" && (
        <>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.navbar.brandName" })}</Label>
            <Input value={block.brandName || ""} onChange={(e) => updateField("brandName", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.navbar.logoUrl" })}</Label>
            <Input value={block.logoUrl || ""} onChange={(e) => updateField("logoUrl", e.target.value)} placeholder="https://..." />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.navbar.logoInitial" })}</Label>
            <Input value={block.logoInitial || ""} onChange={(e) => updateField("logoInitial", e.target.value.slice(0, 2))} />
          </FormGroup>
          {(block.links || []).map((link, index) => (
            <div key={index} className="mb-3 p-2 border rounded">
              <FormGroup>
                <Label>{intl.formatMessage({ id: "app.websitePage.navbar.linkLabel" })}</Label>
                <Input
                  value={link.label}
                  onChange={(e) => {
                    const links = [...(block.links || [])];
                    links[index] = { ...links[index], label: e.target.value };
                    updateField("links", links);
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label>{intl.formatMessage({ id: "app.websitePage.navbar.linkHref" })}</Label>
                <Input
                  value={link.href}
                  onChange={(e) => {
                    const links = [...(block.links || [])];
                    links[index] = { ...links[index], href: e.target.value };
                    updateField("links", links);
                  }}
                />
              </FormGroup>
              <Button
                color="danger"
                size="sm"
                outline
                onClick={() => updateField("links", (block.links || []).filter((_, i) => i !== index))}
              >
                {intl.formatMessage({ id: "app.websitePage.navbar.removeLink" })}
              </Button>
            </div>
          ))}
          <Button
            color="secondary"
            size="sm"
            outline
            className="mb-3"
            onClick={() =>
              updateField("links", [
                ...(block.links || []),
                { label: intl.formatMessage({ id: "app.websitePage.props.newLink" }), href: "#" },
              ])
            }
          >
            {intl.formatMessage({ id: "app.websitePage.navbar.addLink" })}
          </Button>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.navbar.ctaLabel" })}</Label>
            <Input value={block.ctaLabel || ""} onChange={(e) => updateField("ctaLabel", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.navbar.ctaHref" })}</Label>
            <Input value={block.ctaHref || ""} onChange={(e) => updateField("ctaHref", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.navbar.backgroundColor" })}</Label>
            <Input type="color" value={block.backgroundColor || "#ffffff"} onChange={(e) => updateField("backgroundColor", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.navbar.menuBackgroundColor" })}</Label>
            <Input
              type="color"
              value={block.menuBackgroundColor || block.backgroundColor || "#ffffff"}
              onChange={(e) => updateField("menuBackgroundColor", e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.navbar.textColor" })}</Label>
            <Input type="color" value={block.textColor || "#0f172a"} onChange={(e) => updateField("textColor", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.navbar.accentColor" })}</Label>
            <Input type="color" value={block.accentColor || "#4f46e5"} onChange={(e) => updateField("accentColor", e.target.value)} />
          </FormGroup>
          <FormGroup check>
            <Label check>
              <Input
                type="checkbox"
                checked={block.sticky !== false}
                onChange={(e) => updateField("sticky", e.target.checked)}
              />{" "}
              {intl.formatMessage({ id: "app.websitePage.navbar.sticky" })}
            </Label>
          </FormGroup>
        </>
      )}

      {(block.type === "hero" || block.type === "cta") && (
        <>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.props.title" })}</Label>
            <Input value={block.title || ""} onChange={(e) => updateField("title", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.props.subtitle" })}</Label>
            <Input value={block.subtitle || ""} onChange={(e) => updateField("subtitle", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.props.button" })}</Label>
            <Input value={block.buttonLabel || ""} onChange={(e) => updateField("buttonLabel", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.props.buttonLink" })}</Label>
            <Input value={block.buttonHref || ""} onChange={(e) => updateField("buttonHref", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.props.bgColor" })}</Label>
            <Input type="color" value={block.backgroundColor || "#4361ee"} onChange={(e) => updateField("backgroundColor", e.target.value)} />
          </FormGroup>
        </>
      )}

      {block.type === "spacer" && (
        <FormGroup>
          <Label>{intl.formatMessage({ id: "app.websitePage.props.height" })}</Label>
          <Input value={block.height || "48px"} onChange={(e) => updateField("height", e.target.value)} placeholder="48px" />
        </FormGroup>
      )}

      {block.type === "testimonials" && (
        <>
          {(block.items || []).map((item, index) => (
            <div key={index} className="mb-3 p-2 border rounded">
              <FormGroup>
                <Label>{intl.formatMessage({ id: "app.websitePage.props.quote" })}</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={item.quote || ""}
                  onChange={(e) => {
                    const items = [...(block.items || [])];
                    items[index] = { ...items[index], quote: e.target.value };
                    updateField("items", items);
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label>{intl.formatMessage({ id: "app.websitePage.props.author" })}</Label>
                <Input
                  value={item.author || ""}
                  onChange={(e) => {
                    const items = [...(block.items || [])];
                    items[index] = { ...items[index], author: e.target.value };
                    updateField("items", items);
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label>{intl.formatMessage({ id: "app.websitePage.props.role" })}</Label>
                <Input
                  value={item.role || ""}
                  onChange={(e) => {
                    const items = [...(block.items || [])];
                    items[index] = { ...items[index], role: e.target.value };
                    updateField("items", items);
                  }}
                />
              </FormGroup>
              <Button
                size="sm"
                color="danger"
                outline
                onClick={() => updateField("items", (block.items || []).filter((_, i) => i !== index))}
              >
                {intl.formatMessage({ id: "app.websitePage.props.delete" })}
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            color="secondary"
            onClick={() =>
              updateField("items", [...(block.items || []), { quote: "", author: "", role: "" }])
            }
          >
            {intl.formatMessage({ id: "app.websitePage.props.addTestimonial" })}
          </Button>
        </>
      )}

      {block.type === "faq" && (
        <>
          {(block.items || []).map((item, index) => (
            <div key={index} className="mb-3 p-2 border rounded">
              <FormGroup>
                <Label>{intl.formatMessage({ id: "app.websitePage.props.question" })}</Label>
                <Input
                  value={item.question || ""}
                  onChange={(e) => {
                    const items = [...(block.items || [])];
                    items[index] = { ...items[index], question: e.target.value };
                    updateField("items", items);
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label>{intl.formatMessage({ id: "app.websitePage.props.answer" })}</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={item.answer || ""}
                  onChange={(e) => {
                    const items = [...(block.items || [])];
                    items[index] = { ...items[index], answer: e.target.value };
                    updateField("items", items);
                  }}
                />
              </FormGroup>
              <Button
                size="sm"
                color="danger"
                outline
                onClick={() => updateField("items", (block.items || []).filter((_, i) => i !== index))}
              >
                {intl.formatMessage({ id: "app.websitePage.props.delete" })}
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            color="secondary"
            onClick={() =>
              updateField("items", [...(block.items || []), { question: "", answer: "" }])
            }
          >
            {intl.formatMessage({ id: "app.websitePage.props.addQuestion" })}
          </Button>
        </>
      )}

      {block.type === "gallery" && (
        <>
          {(block.images || []).map((img, index) => (
            <div key={index} className="mb-3 p-2 border rounded">
              <FormGroup>
                <Label>
                  {intl.formatMessage({ id: "app.websitePage.props.imageUrlN" }, { n: index + 1 })}
                </Label>
                <Input
                  value={img.src || ""}
                  onChange={(e) => {
                    const images = [...(block.images || [])];
                    images[index] = { ...images[index], src: e.target.value };
                    updateField("images", images);
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label>{intl.formatMessage({ id: "app.websitePage.props.alt" })}</Label>
                <Input
                  value={img.alt || ""}
                  onChange={(e) => {
                    const images = [...(block.images || [])];
                    images[index] = { ...images[index], alt: e.target.value };
                    updateField("images", images);
                  }}
                />
              </FormGroup>
              <Button
                size="sm"
                color="danger"
                outline
                onClick={() => updateField("images", (block.images || []).filter((_, i) => i !== index))}
              >
                {intl.formatMessage({ id: "app.websitePage.props.delete" })}
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            color="secondary"
            className="mb-2"
            onClick={() =>
              updateField("images", [
                ...(block.images || []),
                { src: "https://via.placeholder.com/300x200", alt: "" },
              ])
            }
          >
            {intl.formatMessage({ id: "app.websitePage.props.addImage" })}
          </Button>
        </>
      )}

      {block.type === "image" && (
        <>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.props.imageUrl" })}</Label>
            <Input value={block.src || ""} onChange={(e) => updateField("src", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.editor.uploadImage" })}</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="d-none"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file || !company?.id) return;
                setIsUploading(true);
                try {
                  const uploaded = await websitePageApiService.uploadImage(company.id, file);
                  updateField("src", uploaded.url);
                } catch {
                  toast.error(intl.formatMessage({ id: "app.websitePage.toast.uploadFailed" }));
                } finally {
                  setIsUploading(false);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }
              }}
            />
            <Button
              size="sm"
              color="secondary"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {isUploading
                ? intl.formatMessage({ id: "app.websitePage.editor.uploading" })
                : intl.formatMessage({ id: "app.websitePage.editor.uploadImage" })}
            </Button>
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.props.alt" })}</Label>
            <Input value={block.alt || ""} onChange={(e) => updateField("alt", e.target.value)} />
          </FormGroup>
        </>
      )}

      {block.type === "button" && (
        <>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.props.text" })}</Label>
            <Input value={block.label || ""} onChange={(e) => updateField("label", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.props.link" })}</Label>
            <Input value={block.href || ""} onChange={(e) => updateField("href", e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.props.color" })}</Label>
            <Input type="color" value={block.backgroundColor || "#4361ee"} onChange={(e) => updateField("backgroundColor", e.target.value)} />
          </FormGroup>
        </>
      )}

      {block.type === "video" && (
        <FormGroup>
          <Label>{intl.formatMessage({ id: "app.websitePage.props.embedUrl" })}</Label>
          <Input value={block.embedUrl || ""} onChange={(e) => updateField("embedUrl", e.target.value)} />
        </FormGroup>
      )}

      <div className="wp-properties__section">
        <h6 className="wp-properties__section-title">{intl.formatMessage({ id: "app.websitePage.editor.style" })}</h6>
      <FormGroup>
        <Label>{intl.formatMessage({ id: "app.websitePage.editor.textAlign" })}</Label>
        <Input
          type="select"
          value={block.styles?.textAlign || "left"}
          onChange={(e) => updateStyle("textAlign", e.target.value)}
        >
          <option value="left">{intl.formatMessage({ id: "app.websitePage.props.alignLeft" })}</option>
          <option value="center">{intl.formatMessage({ id: "app.websitePage.props.alignCenter" })}</option>
          <option value="right">{intl.formatMessage({ id: "app.websitePage.props.alignRight" })}</option>
        </Input>
      </FormGroup>
      <FormGroup>
        <Label>{intl.formatMessage({ id: "app.websitePage.editor.bgColor" })}</Label>
        <Input
          type="color"
          value={block.styles?.backgroundColor || "#ffffff"}
          onChange={(e) => updateStyle("backgroundColor", e.target.value)}
        />
      </FormGroup>
      <FormGroup>
        <Label>{intl.formatMessage({ id: "app.websitePage.editor.padding" })}</Label>
        <Input
          value={block.styles?.padding || ""}
          placeholder="24px"
          onChange={(e) => updateStyle("padding", e.target.value)}
        />
      </FormGroup>
      </div>
      </div>
    </div>
  );
}
